import { getCurrencyByLocale, renderPercentage } from "@/apiUtils/commonUtils";
import { ProductType } from "@/apiUtils/constants";
import CurrencyIcon from "@/common/core/icons/CurrencyIcon";
import { TranslationData } from "@/common/lib/types";
import { cn } from "@/lib/tailwind";
import { formatCurrency, getLangFromLocale } from "@/utils/common";
import RenderPrice from "./RenderPrice";

type PriceData = {
  currency?: string;
  originalPrice?: number;
  discountPercentage?: string;
  discountValue?: string | number;
  discountPercent?: number;
  hasDiscount?: boolean;
  price: number;
};

interface IProductPrice {
  mobile?: boolean;
  priceInfo: PriceData;
  className?: string;
  locale: string;
  translations?: TranslationData;
  productType?: string;
  inColumnView?: boolean;
  showDiscountAmount?: boolean;
}

const ProductPrice = ({
  mobile = false,
  priceInfo,
  className,
  locale,
  translations,
  productType,
  inColumnView,
  showDiscountAmount = false,
}: IProductPrice) => {
  const lang = getLangFromLocale(locale);
  priceInfo.currency = getCurrencyByLocale(locale);
  const isFree = priceInfo?.originalPrice! < 1;
  const hasDiscount =
    priceInfo.hasDiscount ??
    !!(priceInfo.discountValue || priceInfo.discountPercentage || priceInfo.discountPercent);

  if (isFree)
    return (
      <div
        className={cn(
          "flex flex-wrap gap-0 font-semibold lg:gap-1",
          mobile ? "flex-col items-start justify-center" : "items-center",
          className,
        )}
      >
        {lang === "en" ? "Free" : "حر"}
      </div>
    );

  return (
    <div
      className={cn(
        "flex flex-wrap gap-0 lg:gap-1",
        mobile
          ? "flex-col items-start justify-center"
          : cn("items-center", !showDiscountAmount && "lg:flex-row lg:items-start lg:flex-nowrap"),
        className,
      )}
    >
      <div className="me-0.5">
        {hasDiscount ? (
          <h3
            className={cn(
              "flex items-center gap-1 whitespace-nowrap text-tertiary-6 line-through",
              !className
                ? inColumnView
                  ? "text-[13px] leading-[18px] md:text-sm"
                  : "text-[11px] leading-[18px] md:text-sm"
                : "",
            )}
          >
            <CurrencyIcon currency={priceInfo.currency || ""} />
            {formatCurrency(String(priceInfo.originalPrice) as string)}
          </h3>
        ) : (
          <h3
            className={cn(
              "flex items-center gap-1 font-semibold text-black",
              !className
                ? inColumnView
                  ? "text-sm leading-[18px]"
                  : "text-xs leading-[18px] md:text-[1em]"
                : "",
            )}
          >
            <CurrencyIcon currency={priceInfo.currency || ""} />
            {formatCurrency(String(priceInfo.originalPrice) as string)}
          </h3>
        )}
      </div>
      {hasDiscount && (
        <div className="me-0.5 flex items-center">
          <h3
            className={cn(
              "flex items-center gap-1 whitespace-nowrap font-semibold text-secondary-1",
              !className
                ? inColumnView
                  ? "ms-1 text-sm leading-[18px]"
                  : "text-xs leading-[18px] md:text-[1em]"
                : "",
            )}
          >
            <CurrencyIcon currency={priceInfo.currency || ""} />
            {formatCurrency(String(priceInfo?.price))}
          </h3>
          {/* TODO(QA): When Qatar (qa) is enabled, gate % (renderPercentage / discountPercentage) with
              NEXT_PUBLIC_PERCENTAGE_SYMBOL_HIDE_COUNTRIES via getPercentHideCountries()/displayPercentage (same as Magrabi). */}
          {!mobile && (
            <h3
              className={cn(
                "ms-1 whitespace-nowrap text-tertiary-6",
                !className
                  ? inColumnView
                    ? "text-[13px] leading-[18px] md:text-sm"
                    : "text-[11px] leading-[18px] md:text-sm"
                  : "",
              )}
            >
              {priceInfo.discountValue ? (
                <RenderPrice
                  price={priceInfo.discountValue}
                  currencyCode={priceInfo.currency || ""}
                  lang={lang}
                  isNegative
                />
              ) : priceInfo.discountPercent !== undefined ? (
                renderPercentage(lang, priceInfo.discountPercent, true)
              ) : priceInfo.discountPercentage ? (
                `-${priceInfo.discountPercentage}%`
              ) : null}
            </h3>
          )}
        </div>
      )}
      {productType === ProductType.EYEGLASSES ? (
        <h3 className="text-xs lg:text-[0.75em]">{translations?.pdp?.pdp_excluding_lens_label}</h3>
      ) : (
        <></>
      )}
    </div>
  );
};

export default ProductPrice;
